import React, { useState, useRef, useEffect } from 'react';
import '../styles/Header.css';
import ScoreList from './ScoreList';
import { logout } from '../utils/logout';
import { LuGem, LuInfo, LuLogOut } from 'react-icons/lu';
import { FcGoogle } from 'react-icons/fc';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import { persistUserSession } from '../utils/authStorage';
import useLocale from '../hooks/useLocale';
import { setLocalePreference } from '../utils/locale';
import useUserPlan from '../hooks/useUserPlan';
import AboutModal from './AboutModal';

const PROFILE_TEXT = {
  ja: {
    upgrade: 'PROプラン',
    about: 'About',
    logout: 'Logout',
  },
  en: {
    upgrade: 'Upgrade',
    about: 'About',
    logout: 'Logout',
  },
};

export default function Header({ user, isMenuOpen = false, setIsMenuOpen, showMenu = true }) {
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const locale = useLocale();
  const { isPro } = useUserPlan(user);
  const profileText = PROFILE_TEXT[locale] || PROFILE_TEXT.en;
  const [isScrolled, setIsScrolled] = useState(false);
  const profileMenuRef = useRef(null);

  const canToggleMenu = typeof setIsMenuOpen === 'function';
  const shouldShowMenu = showMenu && !!user;
  const menuOpen = shouldShowMenu ? (canToggleMenu ? isMenuOpen : internalMenuOpen) : false;
  const handleToggleMenu = () => {
    if (!shouldShowMenu) return;
    if (canToggleMenu) {
      setIsMenuOpen(!isMenuOpen);
    } else {
      setInternalMenuOpen((prev) => !prev);
    }
  };
  const headerShiftClass = menuOpen ? ' shift-right' : '';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleGoogleLogin = async () => {
    try {
      const credential = await signInWithPopup(auth, googleProvider);
      const normalizedUser = persistUserSession(credential.user);
      if (!normalizedUser) {
        throw new Error('Unable to resolve user profile after sign-in');
      }
      window.location.reload();
    } catch (error) {
      console.error('Header login failed:', error);
      window.alert('Login failed. Please try again.');
    }
  };

  const handleLocaleSwitch = (nextLocale) => {
    setLocalePreference(nextLocale);
  };
  const toggleLocale = () => handleLocaleSwitch(locale === 'ja' ? 'en' : 'ja');

  return (
    <>
      <header className={`header${headerShiftClass}${isScrolled ? ' header--scrolled' : ''}`}>
        <div className="header-left">
          {shouldShowMenu && (
            <button
              className={`menu-button${headerShiftClass}`}
              onClick={handleToggleMenu}
              aria-label="Open menu"
            >
              ☰
            </button>
          )}
          <div className={`title logo-title${headerShiftClass}`} onClick={() => window.location.href = '/'}>
            <img src="/logo512.png" alt="Logo" className="logo-icon" />
            <span>Chord Analyzer</span>
            {isPro && <span className="header-plan-pill">PRO</span>}
          </div>
        </div>
        <div className="header-right">
          <button
            type="button"
            className="header-language-toggle"
            onClick={toggleLocale}
            aria-label="Switch language"
          >
            {locale === 'ja' ? 'JA / EN⌄' : 'EN / JA⌄'}
          </button>
          {!user && (
            <button
              type="button"
              className="header-login-button"
              aria-label="Google login"
              onClick={handleGoogleLogin}
            >
              <FcGoogle className="header-login-google-icon" />
              <span>Login</span>
            </button>
          )}
          {user && (
            <div
              className="profile-menu-container"
              ref={profileMenuRef}
            >
              <button
                type="button"
                className="profile-menu-trigger"
                onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                aria-haspopup="menu"
                aria-expanded={isProfileMenuOpen}
              >
                <img
                  src={user.picture || '/logo192.png'}
                  alt="User Avatar"
                  className="user-avatar"
                />
                <span className="user-email">{user.email}</span>
              </button>
              {isProfileMenuOpen && (
                <div className="profile-menu-dropdown" role="menu">
                  <div className="profile-menu-locale" role="group" aria-label="Language selector">
                    <button
                      type="button"
                      className={`profile-locale-button${locale === 'ja' ? ' is-active' : ''}`}
                      onClick={() => handleLocaleSwitch('ja')}
                    >
                      日本語
                    </button>
                    <button
                      type="button"
                      className={`profile-locale-button${locale === 'en' ? ' is-active' : ''}`}
                      onClick={() => handleLocaleSwitch('en')}
                    >
                      English
                    </button>
                  </div>
                  <button
                    type="button"
                    className="profile-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      window.location.href = '/upgrade';
                    }}
                  >
                    <LuGem className="profile-menu-icon" aria-hidden="true" />
                    <span>{profileText.upgrade}</span>
                  </button>
                  <button
                    type="button"
                    className="profile-menu-item"
                    role="menuitem"
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      setIsAboutOpen(true);
                    }}
                  >
                    <LuInfo className="profile-menu-icon" aria-hidden="true" />
                    <span>{profileText.about}</span>
                  </button>
                  <button
                    type="button"
                    className="profile-menu-item profile-menu-item--logout"
                    role="menuitem"
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      logout();
                    }}
                  >
                    <LuLogOut className="profile-menu-icon" aria-hidden="true" />
                    <span>{profileText.logout}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      <AboutModal isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)} />
      {shouldShowMenu && (
        <div className={`side-menu${menuOpen ? ' open' : ''}`}>
          <ScoreList userId={user?.uid || user?.sub} />
        </div>
      )}
    </>
  );
}
